import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import Tab from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LocationModel } from "@NA/Hooks/useLocationFilterSearchCustom";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import * as React from "react";
import ADIColors from "@NA/Utils/ADIColors";
import ErrorIcon from "@NA/Icons/ErrorIcon";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { IconProps } from "@insite/mobius/Icon";
import Hidden from "@insite/mobius/Hidden";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";

interface OwnProps {
    extendedStyles?: LocationSearchFormStyles;
    onSearch: (searchGeoLocationFilter: string, searchLocationFilter: string) => void;
    onLocationSelected: (location: LocationModel) => void;
    locationKnown: boolean;
    showSelectedLocation: boolean;
    getLocationNumberFromPagination: (locationIndex: number) => number;
    openLocationContentDisplay: (location: LocationModel) => void;
    resultCount: number;
    locationSearchFilter: string;
    geoLocationSearchText: string;
    showGeoLocationErrorMessage: boolean;
    geoLocationErrorMessage: React.ReactNode;
    isLoading: boolean;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    setDistanceUnitOfMeasure: (uom: DistanceUnitOfMeasure) => void;
    locations: LocationModel[];
    locationsPagination: PaginationModel | undefined;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setRadius: (radius: number) => void;
    siteMessageResultsErrorMessage: React.ReactNode;
    warehouseDetails?: any;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    nationalInventory: state.ADIGlobalData.ADIUserRoleStore.nationalInventory,
    settings: getSettingsCollection(state),
    warehouseResults: state.ADIGlobalData.wareHouseDataStore.WarehouseModel?.warehouseInventoryRespList,
    nationwideWarehouseResults: state.ADIGlobalData.wareHouseDataStore.WarehouseModel?.warehouseInventoryRespList,
});

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const enum TableTabKeys {
    AvailableToShip = "Available to Ship",
    AvailableForPickUp = "Available for Pick Up",
}

export interface LocationSearchFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    form?: InjectableCss;
    stockNationwideLink?: InjectableCss;
    searchContainer?: InjectableCss;
    searchInputRow?: InjectableCss;
    nationwideTitle?: InjectableCss;
    searcInputRowContainer?: InjectableCss;
    searchInputGridContainer: InjectableCss;
    searchGeoLocationGridItem?: InjectableCss;
    searchGeoLocationText?: TextFieldPresentationProps;
    searchLocationsGridItem?: InjectableCss;
    searchLocationsText?: TextFieldPresentationProps;
    searchButtonGridItem?: InjectableCss;
    searchButton?: ButtonPresentationProps;
    searchResultsTextGridItem?: InjectableCss;
    searchResultsText?: TypographyPresentationProps;
    distanceUnitOfMeasureGridItem?: InjectableCss;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureImperialRadio?: RadioComponentProps;
    distanceUnitOfMeasureMetricRadio?: RadioComponentProps;
    siteMessageGridItem?: InjectableCss;
    siteMessageGeocodeErrorText?: TypographyPresentationProps;
    siteMessageResultsErrorText?: TypographyPresentationProps;
    searchResultGridItem?: InjectableCss;
    searchResultItemContainer?: InjectableCss;
    searchResultRow?: InjectableCss;
    availabilityContent?: InjectableCss;
    availPickup?: InjectableCss;
    availToShip?: InjectableCss;
    availToPickUpContainer?: InjectableCss;
    availToShipContainer?: InjectableCss;
    noResultsContainer?: InjectableCss;
    availabilityNote?: InjectableCss;
    branchLink?: InjectableCss;
    availabilityNoteContainer?: InjectableCss;
    noResultsInnerContainer?: InjectableCss;
    noResultsExpandRadText?: InjectableCss;
    noResultsText?: InjectableCss;
    searchResultGridContainer?: InjectableCss;
    searchResultContainer?: InjectableCss;
    searchResultLocationDetailsGridContainer?: InjectableCss;
    searchResultDisplayNumberGridItem?: InjectableCss;
    locationDisplayNumberText?: TypographyPresentationProps;
    searchResultLocationGridItem?: InjectableCss;
    searchResultCountGridItem?: InjectableCss;
    locationNameText?: TypographyPresentationProps;
    locationNameLink?: LinkPresentationProps;
    searchResultAddressInfoOffsetGridItem?: InjectableCss;
    searchResultAddressInfoGridItem?: InjectableCss;
    locationAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    searchResultLinksAndDistanceContainer?: InjectableCss;
    searchResultPhoneOffsetGridItem?: InjectableCss;
    searchResultPhoneGridItem?: InjectableCss;
    searchResultPhoneLink?: LinkPresentationProps;
    searchResultLinksOffsetGridItem?: InjectableCss;
    searchResultContentGridItem?: InjectableCss;
    locationContentLink?: LinkPresentationProps;
    searchResultGoogleMapsDirectionLinkGridItem?: InjectableCss;
    locationGoogleMapsDirectionLink?: LinkPresentationProps;
    searchResultDistanceGridItem?: InjectableCss;
    locationDistanceDisplayText?: TypographyPresentationProps;
    searchResultStyledWrapperiderGridItem?: InjectableCss;
    searchResultStyledWrapperider?: InjectableCss;
    paginationRow?: InjectableCss;
    pagination?: PaginationPresentationProps;
    errorIcon?: IconProps;
    errorMessageContainer?: InjectableCss;
    siteMessageResultsErrorContainer?: InjectableCss;
    geoLocationErrorMsgStyle?: TypographyPresentationProps;
}

export const locationSearchFormStyles: LocationSearchFormStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    stockNationwideLink: {
        css: css``,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchGeoLocationText: {
        css: css`
            text-transform: capitalize;
        `,
    },
    searchContainer: {
        css: css`
            display: grid;
            margin-bottom: 25px;
        `,
    },
    searcInputRowContainer: {
        css: css`
            display: flex;
            position: relative;
        `,
    },
    searchInputRow: {
        css: css`
            margin-bottom: 25px;
        `,
    },
    nationwideTitle: {
        css: css`
            margin-top: 15px;
            font-size: 18px;
            font-family: "Nunito Sans", sans-serif;
            font-weight: bold;
            line-height: normal;
            color: #002856; // Static color given because this color is not present in Styleguide
        `,
    },
    searchInputGridContainer: {},
    searchGeoLocationGridItem: {
        css: css`
            width: calc(100% - 375px);
            margin-right: 20px !important;
            > div > div[class^="FormInputWrapper"] {
                margin-top: 0 !important;
            }
        `,
    },
    searchLocationsGridItem: {
        css: css`
            width: 100%;
            max-width: 225px;
            margin-right: 20px !important;
            > div > div[class^="FormInputWrapper"] {
                margin-top: 0 !important;
            }
        `,
    },
    searchButtonGridItem: {
        css: css`
            display: inline-block;
            max-width: 191px;
            min-width: 130px;
            flex-direction: row;
            text-align: right;
            > button {
                min-width: 130px;
            }
            & > div {
                display: inline-flex;
            }
        `,
    },
    searchResultsText: {
        css: css`
            margin-top: 10px;
        `,
    },
    searchResultsTextGridItem: {},
    distanceUnitOfMeasureGridItem: {},
    distanceUnitOfMeasureRadioGroup: {
        css: css`
            display: inline-block;
            width: 100%;
            flex-direction: row;
            text-align: right;
            & > div {
                display: inline-flex;
            }
        `,
    },
    siteMessageGridItem: {
        css: css`
            position: relative;
        `,
    },
    siteMessageGeocodeErrorText: {
        color: "danger",
        weight: "bold",
    },
    siteMessageResultsErrorText: {
        color: "danger",
        weight: "bold",
    },
    searchResultCountGridItem: {
        css: css`
            width: 15%;
            text-align: right;
            padding-right: 15px;
            font-size: 14px;
            > span {
                font-size: 14px;
                font-weight: normal;
                font-style: normal;
                font-family: "Nunito Sans", sans-serif;
                line-height: normal;
                text-align: right;
                padding-right: 15px;
                color: ${getColor("text.main")};
                vertical-align: middle;
            }
        `,
    },
    searchResultGridItem: {
        css: css`
            min-height: 49px;
            border: none;
            border-bottom: 1px solid ${getColor("common.border")};
            display: flex;
            align-items: center;
        `,
    },
    searchResultItemContainer: {
        css: css`
            display: block;
            width: 100%;
            border: 1px solid ${getColor("common.border")};
            margin: 0;
            max-height: initial !important;
            min-height: initial;
            border-bottom: none;
            border-top: none;
            &:nth-child(odd) {
                background-color: ${getColor("common.background")};
            }
            &:nth-child(even) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    searchResultRow: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            margin-top: 10px;
        `,
    },
    availabilityContent: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            position: relative;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    availPickup: {
        css: css`
            padding: 8px 16px;
            margin-right: 5px;
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            border: 1px solid ${getColor("common.border")};
            background-color: ${getColor("common.accent")};
            cursor: pointer;
            min-width: 220px;
            width: auto;
            min-height: 49px;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 220px;
                        width: 49.5%;
                        min-width: initial;
                        padding: 15px 12px;
                        white-space: nowrap;
                    `,
                    css`
                        max-width: 220px;
                        width: 49.5%;
                        min-width: initial;
                        padding: 15px 12px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    availToShip: {
        css: css`
            padding: 8px 16px;
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            border: solid 1px ${getColor("common.border")};
            background-color: ${getColor("common.accent")};
            cursor: pointer;
            min-width: 220px;
            width: auto;
            min-height: 49px;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 220px;
                        width: 49%;
                        min-width: initial;
                        padding: 15px 12px;
                        white-space: nowrap;
                    `,
                    css`
                        max-width: 220px;
                        width: 49%;
                        min-width: initial;
                        padding: 15px 12px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    availToPickUpContainer: {
        css: css`
            margin-top: 0 !important;
            border-top: 0 !important;
            border-bottom: 0 !important;
            padding: 0 !important;
        `,
    },
    availToShipContainer: {
        css: css`
            margin-top: 0 !important;
            border-top: 0 !important;
            border-bottom: 0 !important;
            padding: 0 !important;
        `,
    },
    noResultsContainer: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            margin: 0;
            max-height: initial !important;
            min-height: initial;
            border-bottom: none;
            border-top: none;
        `,
    },
    availabilityNoteContainer: {
        css: css`
            padding: 20px 15px !important;
            border: 1px solid ${getColor("common.border")};
            font-size: 16px !important;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    availabilityNote: {
        css: css`
            font-size: 14px !important;
            padding-left: 0 !important;
            font-family: "Nunito Sans", sans-serif;
            color: ${getColor("text.main")} !important;
        `,
    },
    branchLink: {
        css: css`
            text-decoration: underline;
            color: ${getColor("text.link")};
        `,
    },
    noResultsInnerContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            flex-direction: column;
            height: auto;
            padding: 60px 10px;
            background-color: ${getColor("common.background")};
            width: 100%;
            padding-right: 0;
            max-height: 245px;
        `,
    },
    noResultsExpandRadText: {
        css: css`
            font-size: 14px;
            font-weight: normal;
            line-height: normal;
            text-align: center;
            font-family: "Nunito Sans", sans-serif;
            color: ${getColor("text.main")};
        `,
    },
    noResultsText: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            line-height: normal;
            font-family: "Nunito Sans", sans-serif;
            text-align: center;
            color: ${getColor("text.main")};
        `,
    },
    searchResultContainer: {
        css: css`
            > div[class^="TabGroupWrapper"] {
                padding: 0 !important;
                ul {
                    padding: 0 !important;
                    border-bottom: 1px solid ${getColor("common.border")};
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                overflow-x: hidden;
                            `,
                    css`
                                overflow-x: hidden;
                            `,
                    null,
                    null,
                    null,
                ])}
                    li:focus {
                        outline-color: none !important;
                        outline-style: solid !important;
                        outline-width: 0 !important;
                    }
                    li: hover {
                        border-bottom: 0 !important;
                    }
                    li[aria-selected="true"] {
                        background-color: ${getColor("common.background")};
                        border-bottom: none;
                    }
                    li {
                        span {
                            font-size: 14px !important;
                            font-weight: 600 !important;
                        }
                    }
                }
                div {
                    div[class^="TabContent"] {
                        margin-top: 0 !important;
                        border-top: 0 !important;
                        border-bottom: 0 !important;
                        padding: 0 !important;
                        max-height: 244px;
                        overflow-y: auto;
                    }
                }
            }
        `,
    },
    searchResultGridContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
        `,
    },
    searchResultLocationDetailsGridContainer: {},
    searchResultDisplayNumberGridItem: {
        css: css`
            justify-content: center;
        `,
    },
    locationDisplayNumberText: {
        color: "primary",
        weight: "bold",
    },
    searchResultLocationGridItem: {
        css: css`
            width: 85%;
          
        `,
    },
    locationNameText: {
        color: "primary",
        weight: "bold",
        ellipsis: true,
    },
    locationNameLink: {
        color: "primary",
        typographyProps: {
            weight: "bold",
            ellipsis: true,
        },
        css: css`
            width: 85%;
            &:focus {
                outline: none;
            }
            > span {
                font-size: 14px;
                font-weight: normal;
                font-style: normal;
                font-family: "Nunito Sans", sans-serif;
                line-height: normal;
                text-align: left;
                color: ${getColor("text.main")} !important;
                padding-left: 15px;
                text-transform: capitalize;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                width: 100%;
                outline: none;
            }
        `,
    },
    searchResultAddressInfoOffsetGridItem: {
        css: css`
            justify-content: center;
        `,
    },
    searchResultAddressInfoGridItem: {},
    searchResultPhoneOffsetGridItem: {},
    searchResultPhoneGridItem: {},
    searchResultLinksAndDistanceContainer: {
        css: css`
            width: 100%;
            padding: 0;
            margin: 0;
        `,
    },
    searchResultLinksOffsetGridItem: {},
    searchResultContentGridItem: {},
    searchResultGoogleMapsDirectionLinkGridItem: {},
    searchResultDistanceGridItem: {},
    searchResultStyledWrapperiderGridItem: {},
    searchResultStyledWrapperider: {
        css: css`
            border: 1px solid #4a4a4a33; // Static color given because this color is not present in Styleguide
            width: 100%;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: center;
            `,
            perPageSelect: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "min",
                    )}
            `,
        },
    },
    errorIcon: {
        css: css`
            height: 43px;
        `,
    },
    errorMessageContainer: {
        css: css`
            position: absolute;
            top: 47px;
            > svg {
                height: 13px;
            }
        `,
    },
    siteMessageResultsErrorContainer: {
        css: css`
            position: absolute;
            top: 8px;
            > svg {
                height: 13px;
            }
        `,
    },
    geoLocationErrorMsgStyle: {
        css: css`
            font-size: 14px;
            margin-left: 5px;
            color: ${ADIColors.darkBlack3};
        `,
    },
};

const StyledForm = getStyledWrapper("form");

const Container = StyledWrapper;
const ContainerCell = StyledWrapper;

const ADILocationSearchForm: React.FC<Props> = ({
    extendedStyles,
    onSearch,
    locationKnown,
    locationSearchFilter,
    geoLocationSearchText,
    showGeoLocationErrorMessage,
    geoLocationErrorMessage,
    isLoading,
    locations,
    siteMessageResultsErrorMessage,
    warehouseResults,
    nationwideWarehouseResults,
    setRadius,
    nationalInventory,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationSearchFormStyles, extendedStyles));
    const [searchLocationFilter, setSearchLocationFilter] = React.useState(locationSearchFilter);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);
    const [filterHubs, setFilterHubs] = React.useState<any[]>([]);
    const [nationwideFilterHubs, setNationwideFilterHubs] = React.useState<any[]>([]);
    const [nationwideFilterBranches, setNationwideFilterBranches] = React.useState<any[]>([]);
    const [newFilterBranches, setNewFilterBranches] = React.useState<any[]>([]);
    const [inventoryAvailability, setInventoryAvailability] = React.useState<any[]>([]);
    const [enableNationalInventory, setEnableNationalInventory] = React.useState(true);
    const [filterInventoryAvailability, setFilterInventoryAvailability] = React.useState([]);
    const [retainBranches, setRetainBranches] = React.useState([]);
    const [retainHubs, setRetainHubs] = React.useState([]);
    const [isShown, setIsShown] = React.useState(false);
    const [currentTab, setCurrentTab] = React.useState(TableTabKeys.AvailableForPickUp);
    const [geoLocationError, setGeoLocationError] = React.useState(false);

    React.useEffect(() => {
        const filterBranches: any[] = [];
        // eslint-disable-next-line array-callback-return


        locations.forEach((location, index) => {
            const adminDC = location.properties.admnDc;
            warehouseResults?.map((warehouseResult, index) => {
                if (adminDC === warehouseResult.warehouse.name) {
                    const newWareHouseResult = warehouseResult;
                    filterBranches.push({
                        location: `${newWareHouseResult.warehouse.description.toLowerCase()},  ${newWareHouseResult.warehouse.state
                            }`,
                        qty: warehouseResult.inventory,
                        name: warehouseResult.warehouse.name,
                    });
                } else if ((adminDC === undefined || adminDC === "") && location.state.toLowerCase() === "pr") {
                    const newWareHouseResult = warehouseResult;
                    filterBranches.push({
                        location: `${newWareHouseResult.warehouse.description.toLowerCase()},  ${newWareHouseResult.warehouse.state
                            }`,
                        qty: warehouseResult.inventory,
                        name: warehouseResult.warehouse.name,
                    });
                }
            });
            setInventoryAvailability(filterBranches);
        });
    }, [locations, warehouseResults]);


    const processWarehouseData = () => {
        const newFilterBranches: any[] = [];
        const filterHubs: any[] = [];

        inventoryAvailability?.forEach((inventoryAvailable) => {
            const row = inventoryAvailable;
            if (row.location.toLowerCase().indexOf("hub") === -1) {
                newFilterBranches.push(row);
            }
        });

        if (warehouseResults && warehouseResults.length > 0) {
            warehouseResults.forEach((warehouseResult) => {
                const warehouseName = warehouseResult.warehouse.description.toLowerCase();
                if (warehouseName.indexOf("hub") !== -1 && warehouseResult.inventory > 0) {
                    filterHubs.push({
                        location: `${warehouseResult.warehouse.description.toLowerCase()}, ${warehouseResult.warehouse.state}`,
                        qty: warehouseResult.inventory,
                        name: warehouseResult.warehouse.name,
                    });
                }
            });
        }

        setNewFilterBranches(newFilterBranches);
        setFilterHubs(filterHubs);
    };

    React.useEffect(() => {

        processWarehouseData();

    }, [inventoryAvailability, warehouseResults]);

    React.useEffect(() => {
        setSearchLocationFilter(locationSearchFilter);
    }, [locationSearchFilter]);
    React.useEffect(() => {
        setSearchGeoLocationFilter(geoLocationSearchText);
    }, [geoLocationSearchText]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSearch(searchGeoLocationFilter, searchLocationFilter);
    };
    const handleSearchGeoLocationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGeoLocationFilter(event.target.value);
    };

    const showNationalInventory = () => {
        if (enableNationalInventory) {
            setIsShown(current => !current);

            const a: any[] = [];
            warehouseResults?.map((warehouseResult, index) => {
                const newWareHouseResult = warehouseResult;
                a.push({
                    location: `${newWareHouseResult.warehouse.description.toLowerCase()},  ${newWareHouseResult.warehouse.state
                        }`,
                    qty: warehouseResult.inventory,
                    name: warehouseResult.warehouse.name,
                });
            });

            const tempInventory = nationwideWarehouseResults?.map(v => {
                return v.warehouse;
            });

            const tempLocationQty = a?.filter(v => v.qty !== 0);

            setFilterInventoryAvailability(tempLocationQty as any);
            const tempFilterInventoryAvailability = tempLocationQty as any;

            setRetainBranches([]);
            setRetainHubs([]);

            const tempRetainBranches = newFilterBranches;
            const tempRetainHubs = filterHubs;

            const nationwideFilterBranches = [];
            const nationwideFilterHubs = [];

            for (let j = 0; j < tempFilterInventoryAvailability.length; j++) {
                const row = tempFilterInventoryAvailability[j];
                if (row.location.toLowerCase().indexOf("hub") === -1) {
                    nationwideFilterBranches.push(row);
                } else {
                    nationwideFilterHubs.push(row);
                }
            }

            setNationwideFilterBranches(nationwideFilterBranches);
            setNationwideFilterHubs(nationwideFilterHubs);
        }
    };

    React.useEffect(() => {
        setTimeout(() => {
            if (inventoryAvailability.length === 0) {
                setGeoLocationError(true);
            }
        }, 10000);
    }, []);

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey as TableTabKeys);
        }
    };

    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
            {!isShown && (
                <Container {...styles.searchContainer}>
                    <ContainerCell {...styles.searchInputRow}>
                        <Hidden below="md">
                            <StyledWrapper {...styles.searcInputRowContainer}>
                                <StyledWrapper {...styles.searchGeoLocationGridItem}>
                                    <TextField
                                        id="searchGeoLocation"
                                        {...styles.searchGeoLocationText}
                                        required={true}
                                        value={searchGeoLocationFilter}
                                        onChange={handleSearchGeoLocationChanged}
                                        placeholder={translate("Search by Postal Code, Province or Country")}
                                        data-test-selector="locationSearchForm_searchGeoLocation"
                                    />
                                    {showGeoLocationErrorMessage && (
                                        <StyledWrapper {...styles.errorMessageContainer}>
                                            <ErrorIcon />
                                            <Typography {...styles.geoLocationErrorMsgStyle}>
                                                {geoLocationErrorMessage}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchLocationsGridItem}>
                                    <Select
                                        name="miles"
                                        id="miles"
                                        onChange={e => {
                                            setRadius(Number(e.target.value));
                                        }}
                                    >
                                        <option value="100">Within 100 Miles</option>
                                        <option value="200">Within 200 Miles</option>
                                        <option value="300">Within 300 Miles</option>
                                        <option value="500">Within 500 Miles</option>
                                    </Select>
                                </StyledWrapper>
                                <StyledWrapper {...styles.searchButtonGridItem}>
                                    <Button
                                        {...styles.searchButton}
                                        type="submit"
                                        data-test-selector="locationSearchForm_searchButton"
                                    >
                                        {translate("Search")}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        </Hidden>
                        <Hidden above="sm">
                            <GridContainer>
                                <GridItem width={12}>
                                    <TextField
                                        id="searchGeoLocation"
                                        {...styles.searchGeoLocationText}
                                        required={true}
                                        value={searchGeoLocationFilter}
                                        onChange={handleSearchGeoLocationChanged}
                                        placeholder={translate("Search by Postal Code, Province or Country")}
                                        data-test-selector="locationSearchForm_searchGeoLocation"
                                    />
                                </GridItem>
                                <GridItem width={12}>
                                    <StyledWrapper {...styles.searchLocationsGridItem}>
                                        <Select
                                            name="miles"
                                            id="miles"
                                            onChange={e => {
                                                setRadius(Number(e.target.value));
                                            }}
                                        >
                                            <option value="100">Within 100 Miles</option>
                                            <option value="200">Within 200 Miles</option>
                                            <option value="300">Within 300 Miles</option>
                                            <option value="500">Within 500 Miles</option>
                                        </Select>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.searchButtonGridItem}>
                                        <Button
                                            {...styles.searchButton}
                                            type="submit"
                                            data-test-selector="locationSearchForm_searchButton"
                                        >
                                            {translate("Search")}
                                        </Button>
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem width={12}>
                                    {showGeoLocationErrorMessage && (
                                        <StyledWrapper>
                                            <ErrorIcon />
                                            <Typography {...styles.geoLocationErrorMsgStyle}>
                                                {geoLocationErrorMessage}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </Hidden>
                        <StyledWrapper {...styles.searchInputGridContainer}>
                            <StyledWrapper {...styles.siteMessageGridItem}>
                                {geoLocationError && !isLoading && !locationKnown && geoLocationSearchText.length > 0 && (
                                    <>
                                        <Typography {...styles.siteMessageGeocodeErrorText}>
                                            {siteMessage("PickUpLocation_GeocodeErrorMessage")}
                                        </Typography>
                                    </>
                                )}
                                {!isLoading &&
                                    locationKnown &&
                                    locations.length === 0 &&
                                    siteMessageResultsErrorMessage && (
                                        <>
                                            <StyledWrapper {...styles.siteMessageResultsErrorContainer}>
                                                <ErrorIcon />
                                                <Typography {...styles.geoLocationErrorMsgStyle}>
                                                    {siteMessageResultsErrorMessage}
                                                </Typography>
                                            </StyledWrapper>
                                        </>
                                    )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </ContainerCell>

                    <ContainerCell {...styles.searchResultRow}>
                        <StyledWrapper {...styles.searchResultContainer}>
                            {isLoading && locations.length === 0 && (
                                <StyledWrapper {...styles.searchResultGridItem}>
                                    <StyledWrapper {...styles.centeringWrapper}>
                                        <LoadingSpinner {...styles.spinner} />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                            {!isLoading && locationKnown && locations.length !== 0 && (
                                <TabGroup
                                    current={currentTab}
                                    onTabChange={onTabChange}
                                    {...styles.availabilityContent}
                                >
                                    <Tab
                                        headline={`Available for Pick Up`}
                                        tabKey={TableTabKeys.AvailableForPickUp}
                                        key={TableTabKeys.AvailableForPickUp}
                                        {...styles.availPickup}
                                    >
                                        <StyledWrapper {...styles.availToPickUpContainer}>
                                            {newFilterBranches.map((newFilterBranches, index) => {
                                                return (
                                                    <StyledWrapper
                                                        key={newFilterBranches.id}
                                                        {...styles.searchResultItemContainer}
                                                    >
                                                        <StyledWrapper {...styles.searchResultGridItem}>
                                                            <StyledWrapper
                                                                {...styles.searchResultGridContainer}
                                                                data-test-selector="locationSearchForm_location"
                                                            >
                                                                <StyledWrapper {...styles.searchResultLocationGridItem}>
                                                                    <Link
                                                                        {...styles.locationNameLink}
                                                                        data-test-selector="findLocationModal_locationSelect"
                                                                    >
                                                                        {newFilterBranches.location}
                                                                    </Link>
                                                                </StyledWrapper>
                                                                <StyledWrapper {...styles.searchResultCountGridItem}>
                                                                    {newFilterBranches.qty}
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                );
                                            })}
                                        </StyledWrapper>
                                    </Tab>

                                    <Tab
                                        headline={`Available to Ship`}
                                        tabKey={TableTabKeys.AvailableToShip}
                                        key={TableTabKeys.AvailableToShip}
                                        {...styles.availToShip}
                                    >
                                        <StyledWrapper {...styles.availToShipContainer}>
                                            {filterHubs.length > 0 ? (
                                                filterHubs.map((filterHubs, index) => {
                                                    return (
                                                        <StyledWrapper
                                                            key={filterHubs.id}
                                                            {...styles.searchResultItemContainer}
                                                        >
                                                            <StyledWrapper {...styles.searchResultGridItem}>
                                                                <StyledWrapper
                                                                    {...styles.searchResultGridContainer}
                                                                    data-test-selector="locationSearchForm_location"
                                                                >
                                                                    <StyledWrapper
                                                                        {...styles.searchResultLocationGridItem}
                                                                    >
                                                                        <Link
                                                                            {...styles.locationNameLink}
                                                                            data-test-selector="findLocationModal_locationSelect"
                                                                        >
                                                                            {filterHubs.location} DISTRIBUTION CENTER
                                                                        </Link>
                                                                    </StyledWrapper>
                                                                    <StyledWrapper
                                                                        {...styles.searchResultCountGridItem}
                                                                    >
                                                                        {filterHubs.qty}
                                                                    </StyledWrapper>
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    );
                                                })
                                            ) : (
                                                <StyledWrapper {...styles.noResultsContainer}>
                                                    <StyledWrapper {...styles.noResultsInnerContainer}>
                                                        <StyledWrapper {...styles.noResultsText}>
                                                            {translate("No results found.")}
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.noResultsExpandRadText}>
                                                            {translate(
                                                                "Please expand your radius to find your nearest location.",
                                                            )}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            )}
                                        </StyledWrapper>
                                    </Tab>
                                </TabGroup>
                            )}
                            <StyledWrapper {...styles.availabilityNoteContainer}>
                                <StyledWrapper {...styles.availabilityNote}>
                                    Product availability will change based on pending orders. If quantity is low, call{" "}
                                    <Link href="/dealerlocator" {...styles.branchLink}>
                                        {" "}
                                        your local branch{" "}
                                    </Link>{" "}
                                    to confirm availability.
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </ContainerCell>
                </Container>
            )}
            {nationalInventory ? (
                <Link {...styles.stockNationwideLink} onClick={() => showNationalInventory()}>
                    {!isShown ? "View all stock Nationwide" : "Back to Search by Location"}
                </Link>
            ) : (
                ""
            )}

            {isShown && (
                <Container>
                    <StyledWrapper {...styles.nationwideTitle}>Nationwide</StyledWrapper>
                    <ContainerCell {...styles.searchInputRow}>
                        <StyledWrapper {...styles.searchInputGridContainer}>
                            <StyledWrapper {...styles.siteMessageGridItem}>
                                {geoLocationError && !isLoading && !locationKnown && geoLocationSearchText.length > 0 && (
                                    <>
                                        <Typography {...styles.siteMessageGeocodeErrorText}>
                                            {siteMessage("PickUpLocation_GeocodeErrorMessage")}
                                        </Typography>
                                    </>
                                )}
                                {!isLoading &&
                                    locationKnown &&
                                    locations.length === 0 &&
                                    siteMessageResultsErrorMessage && (
                                        <Typography
                                            {...styles.siteMessageResultsErrorText}
                                            data-test-selector="locationSearchForm_siteErrorMessage"
                                        >
                                            {siteMessageResultsErrorMessage}
                                        </Typography>
                                    )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </ContainerCell>

                    <ContainerCell {...styles.searchResultRow}>
                        <StyledWrapper {...styles.searchResultContainer}>
                            {isLoading && locations.length === 0 && (
                                <StyledWrapper {...styles.searchResultGridItem}>
                                    <StyledWrapper {...styles.centeringWrapper}>
                                        <LoadingSpinner {...styles.spinner} />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                            <TabGroup {...styles.availabilityContent}>
                                <Tab
                                    headline={`Available for Pick Up [${nationwideFilterBranches.length}]`}
                                    tabKey="Available for Pick Up"
                                    {...styles.availPickup}
                                >
                                    <StyledWrapper {...styles.availToPickUpContainer}>
                                        {nationwideFilterBranches.length > 0 ? (
                                            nationwideFilterBranches.map((nationwideFilterBranches, index) => {
                                                return (
                                                    <StyledWrapper
                                                        key={nationwideFilterBranches.id}
                                                        {...styles.searchResultItemContainer}
                                                    >
                                                        <StyledWrapper {...styles.searchResultGridItem}>
                                                            <StyledWrapper
                                                                {...styles.searchResultGridContainer}
                                                                data-test-selector="locationSearchForm_location"
                                                            >
                                                                <StyledWrapper {...styles.searchResultLocationGridItem}>
                                                                    <Link
                                                                        {...styles.locationNameLink}
                                                                        data-test-selector="findLocationModal_locationSelect"
                                                                    >
                                                                        {nationwideFilterBranches.location}
                                                                    </Link>
                                                                </StyledWrapper>
                                                                <StyledWrapper {...styles.searchResultCountGridItem}>
                                                                    {nationwideFilterBranches.qty}
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                );
                                            })
                                        ) : (
                                            <StyledWrapper {...styles.noResultsContainer}>
                                                <StyledWrapper {...styles.noResultsInnerContainer}>
                                                    <StyledWrapper {...styles.noResultsText}>
                                                        {translate("No items available for pick up.")}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </Tab>

                                <Tab
                                    headline={`Available to Ship [${nationwideFilterHubs.length}]`}
                                    tabKey="Available to Ship"
                                    {...styles.availToShip}
                                >
                                    <StyledWrapper {...styles.availToShipContainer}>
                                        {nationwideFilterHubs.length > 0 ? (
                                            nationwideFilterHubs.map((nationwideFilterHubs, index) => {
                                                return (
                                                    <StyledWrapper
                                                        key={nationwideFilterHubs.id}
                                                        {...styles.searchResultItemContainer}
                                                    >
                                                        <StyledWrapper {...styles.searchResultGridItem}>
                                                            <StyledWrapper
                                                                {...styles.searchResultGridContainer}
                                                                data-test-selector="locationSearchForm_location"
                                                            >
                                                                <StyledWrapper {...styles.searchResultLocationGridItem}>
                                                                    <Link
                                                                        {...styles.locationNameLink}
                                                                        data-test-selector="findLocationModal_locationSelect"
                                                                    >
                                                                        {nationwideFilterHubs.location} DISTRIBUTION
                                                                        CENTER
                                                                    </Link>
                                                                </StyledWrapper>
                                                                <StyledWrapper {...styles.searchResultCountGridItem}>
                                                                    {nationwideFilterHubs.qty}
                                                                </StyledWrapper>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                );
                                            })
                                        ) : (
                                            <StyledWrapper {...styles.noResultsContainer}>
                                                <StyledWrapper {...styles.noResultsInnerContainer}>
                                                    <StyledWrapper {...styles.noResultsText}>
                                                        {translate("No items available to ship.")}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </Tab>
                            </TabGroup>

                            <StyledWrapper {...styles.availabilityNoteContainer}>
                                <StyledWrapper {...styles.availabilityNote}>
                                    Product availability will change based on pending orders. If quantity is low, call{" "}
                                    <Link href="/dealerlocator" {...styles.branchLink}>
                                        {" "}
                                        your local branch{" "}
                                    </Link>{" "}
                                    to confirm availability.
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </ContainerCell>
                </Container>
            )}
        </StyledForm>
    );

};

export default connect(mapStateToProps, mapDispatchToProps)(ADILocationSearchForm);
